import React from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { CartProvider } from './context/CartContext';
import { RMACartProvider } from './context/RMACartContext';
import { FavoritesProvider, MachinesProvider } from './context/PortalContext';
import { RecentlyViewedProvider, CompareProvider } from './context/ProductContext';
import { AuthProvider } from './context/AuthContext';
import { AdminProvider } from './context/AdminContext';
import { NotificationsProvider } from './context/NotificationsContext';
import { AdminNotificationsProvider } from './context/AdminNotificationsContext';
import { loadRealProductImages } from './data/productImages';
import { ImpersonationBanner } from './components/admin/ImpersonationBanner';
import ProtectedRoute from './components/auth/ProtectedRoute';
import ScrollToTop from './components/ScrollToTop';
import { CompareDrawer } from './components/product/CompareDrawer';
import { CompareBar } from './components/product/CompareBar';
import { Header } from './components/layout/Header';
import { Footer } from './components/layout/Footer';
import { CartDrawer } from './components/layout/CartDrawer';
import { ReadOnlyOperatoreBanner } from './components/layout/ReadOnlyOperatoreBanner';
import { RMACartFloating } from './components/rma/RMACartFloating';
import { PromoBanner } from './components/layout/PromoBanner';
import HomePage from './pages/HomePage';
import ProductSearchPage from './pages/ProductSearchPage';
import ProductDetailPage from './pages/ProductDetailPage';
import CategoryInfoPage from './pages/CategoryInfoPage';
import { GuestCategoriesOverview } from './components/product/GuestCategoriesOverview';
import KtekPage from './pages/KtekPage';
import BrandsPage from './pages/BrandsPage';
import CompanyPage from './pages/CompanyPage';
import SupportPage from './pages/SupportPage';
import ContactPage from './pages/ContactPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import PasswordDimenticataPage from './pages/PasswordDimenticataPage';
import ReimpostaPasswordPage from './pages/ReimpostaPasswordPage';
import LegalPage from './pages/LegalPage';
import NotFoundPage from './pages/NotFoundPage';
// Portal pages
import DashboardPage from './pages/portal/DashboardPage';
import CustomerLayout from './pages/portal/CustomerLayout';
import DatiFatturazionePage from './pages/portal/DatiFatturazionePage';
import DatiAccessoPage from './pages/portal/DatiAccessoPage';
import IndirizziSpedizionePage from './pages/portal/IndirizziSpedizionePage';
import PreferenzePage from './pages/portal/PreferenzePage';
import OperatoriPage from './pages/portal/OperatoriPage';
import ParcoMacchinePage from './pages/portal/ParcoMacchinePage';
import FavoritesPage from './pages/portal/FavoritesPage';
import OrdersPage from './pages/portal/OrdersPage';
import OrderDetailPage from './pages/portal/OrderDetailPage';
import DocumentDetailPage from './pages/portal/DocumentDetailPage';
import DocumentsPage from './pages/portal/DocumentsPage';
import StoricoAcquistiPage from './pages/portal/StoricoAcquistiPage';
import RMAPage from './pages/portal/RMAPage';
import RMADetailPage from './pages/portal/RMADetailPage';
import RMAOrderPage from './pages/portal/RMAOrderPage';
import RMASearchPage from './pages/portal/RMASearchPage';
import RMADocumentSelectPage from './pages/portal/RMADocumentSelectPage';
import RMADocumentItemsPage from './pages/portal/RMADocumentItemsPage';
import RMACartPage from './pages/portal/RMACartPage';
import NotificationsPage from './pages/portal/NotificationsPage';
import OutletPage from './pages/portal/OutletPage';
import OffertePage from './pages/OffertePage';
import CheckoutPage from './pages/portal/CheckoutPage';
import PaymentRedirectPage from './pages/portal/PaymentRedirectPage';
import PaymentResultPage from './pages/portal/PaymentResultPage';
import PaymentCancelledPage from './pages/portal/PaymentCancelledPage';
import WaitingListPage from './pages/portal/WaitingListPage';
// Admin pages
import AdminLayout from './pages/admin/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminBannerPage from './pages/admin/AdminBannerPage';
import AdminProductsPage from './pages/admin/AdminProductsPage';
import AdminProductDetailPage from './pages/admin/AdminProductDetailPage';
import AdminCareersPage from './pages/admin/AdminCareersPage';
import AdminCareerDetailPage from './pages/admin/AdminCareerDetailPage';
import CareerDetailPage from './pages/CareerDetailPage';
import AdminAlertStockPage from './pages/admin/AdminAlertStockPage';
import AdminCarrelliPage from './pages/admin/AdminCarrelliPage';
import AdminDifformityPage from './pages/admin/AdminDifformityPage';
import AdminDifformityDetailPage from './pages/admin/AdminDifformityDetailPage';
import AdminRMAPage from './pages/admin/AdminRMAPage';
import AdminRMADetailPage from './pages/admin/AdminRMADetailPage';
import AdminOrdersPage from './pages/admin/AdminOrdersPage';
import AdminNotificationsPage from './pages/admin/AdminNotificationsPage';
import AdminHomepageProductsPage from './pages/admin/AdminHomepageProductsPage';
import AdminOrderDetailPage from './pages/admin/AdminOrderDetailPage';
import AdminErpOrderDetailPage from './pages/admin/AdminErpOrderDetailPage';
import AdminClientsPage from './pages/admin/AdminClientsPage';
import AdminClientDetailPage from './pages/admin/AdminClientDetailPage';
import AdminClientAccessHistoryPage from './pages/admin/AdminClientAccessHistoryPage';
import AdminRegistrationsPage from './pages/admin/AdminRegistrationsPage';
import AdminRegistrationDetailPage from './pages/admin/AdminRegistrationDetailPage';
import AdminPricingOverviewPage from './pages/admin/AdminPricingOverviewPage';
import AdminImpersonatePage from './pages/admin/AdminImpersonatePage';
import AdminAggiornamentiPage from './pages/admin/AdminAggiornamentiPage';
import AbbinatoreV2Routes from './pages/admin/abbinatore-v2/AbbinatoreV2Routes';
import AdminConfigPage from './pages/admin/AdminConfigPage';
import AdminEmailSettingsPage from './pages/admin/AdminEmailSettingsPage';
import AdminHome from './pages/admin/AdminHome';
import AdminTeamPage from './pages/admin/AdminTeamPage';

/**
 * Header globale del sito condizionato: nascosto sulle route che si comportano
 * come "applicazione separata" (es. ``/admin/abbinatore-v2/*``).
 */
const STANDALONE_PREFIXES = ['/admin/abbinatore-v2'];

function useIsStandalone(): boolean {
  const { pathname } = useLocation();
  return STANDALONE_PREFIXES.some(p => pathname.startsWith(p));
}

function ConditionalHeader() {
  if (useIsStandalone()) return null;
  return <Header />;
}

function ConditionalPromoBanner() {
  if (useIsStandalone()) return null;
  return <PromoBanner />;
}

function ConditionalImpersonationBanner() {
  if (useIsStandalone()) return null;
  return <ImpersonationBanner />;
}

function ConditionalFooter() {
  if (useIsStandalone()) return null;
  return <Footer />;
}

function ConditionalReadOnlyOperatoreBanner() {
  if (useIsStandalone()) return null;
  return <ReadOnlyOperatoreBanner />;
}

export default function App() {
  React.useEffect(() => {
    loadRealProductImages(process.env.REACT_APP_BACKEND_URL || '');
  }, []);

  return (
    <BrowserRouter>
      <ScrollToTop />
      <AuthProvider>
      <AdminProvider>
      <NotificationsProvider>
      <AdminNotificationsProvider>
      <CartProvider>
      <RMACartProvider>
      <FavoritesProvider>
      <MachinesProvider>
      <RecentlyViewedProvider>
      <CompareProvider>
      <div className="min-h-screen bg-[hsl(var(--dtk-canvas))] flex flex-col" data-testid="app-root">
        <Toaster position="bottom-right" richColors closeButton duration={2500} />
        <ConditionalImpersonationBanner />
        <ConditionalPromoBanner />
        <ConditionalHeader />
        {/* Dopo l'header, non prima: l'header è sticky e calcola il proprio
            `top` da `--impersonation-banner-h`. Questa striscia sta nel
            flusso normale e non entra in quel calcolo. */}
        <ConditionalReadOnlyOperatoreBanner />
        <main className="flex-1">
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/prodotti/:categoryId/:sku" element={<ProductDetailPage />} />
            <Route path="/prodotti/:categoryId" element={<ProductSearchPage />} />
            <Route path="/i-nostri-prodotti" element={<GuestCategoriesOverview />} />
            <Route path="/i-nostri-prodotti/:categorySlug" element={<CategoryInfoPage />} />
            <Route path="/ktek/:pageId" element={<KtekPage />} />
            <Route path="/ktek" element={<KtekPage />} />
            <Route path="/marche" element={<BrandsPage />} />
            <Route path="/outlet" element={<OutletPage />} />
            <Route path="/offerte" element={<OffertePage />} />
            <Route path="/consumabili-per/:modelId" element={<ProductSearchPage />} />
            <Route path="/azienda/lavora-con-noi/:slug" element={<CareerDetailPage />} />
            <Route path="/azienda/:pageId" element={<CompanyPage />} />
            <Route path="/azienda" element={<CompanyPage />} />
            <Route path="/supporto/:pageId" element={<SupportPage />} />
            <Route path="/supporto" element={<SupportPage />} />
            <Route path="/contatti" element={<ContactPage />} />
            <Route path="/accedi" element={<LoginPage />} />
            <Route path="/registrati" element={<RegisterPage />} />
            <Route path="/password-dimenticata" element={<PasswordDimenticataPage />} />
            <Route path="/reimposta-password" element={<ReimpostaPasswordPage />} />
            <Route path="/legale/:pageId" element={<LegalPage />} />
            <Route path="/legale" element={<LegalPage />} />
            {/* Portal / Area Clienti — richiede autenticazione cliente o agente.
                Admin accede SOLO tramite impersonazione (token swappato → ruolo='cliente'). */}
            <Route element={<ProtectedRoute roles={['cliente', 'agente', 'cliente_subordinato']} roleRedirectTo="/admin"><CustomerLayout /></ProtectedRoute>}>
              <Route path="/area-clienti" element={<DashboardPage />} />
              <Route path="/area-clienti/dati-accesso" element={<DatiAccessoPage />} />
              <Route path="/area-clienti/dati-fatturazione" element={<DatiFatturazionePage />} />
              <Route path="/area-clienti/indirizzi-spedizione" element={<IndirizziSpedizionePage />} />
              <Route path="/area-clienti/parco-macchine" element={<ParcoMacchinePage />} />
              <Route path="/area-clienti/preferiti" element={<FavoritesPage />} />
              <Route path="/area-clienti/ordini" element={<OrdersPage />} />
              <Route path="/area-clienti/ordini/:tipork/:anno/:serie/:numdoc" element={<OrderDetailPage />} />
              <Route path="/area-clienti/documenti" element={<DocumentsPage />} />
              <Route path="/area-clienti/documenti/:tipork/:anno/:serie/:numdoc" element={<DocumentDetailPage />} />
              <Route path="/area-clienti/storico-acquisti" element={<StoricoAcquistiPage />} />
              <Route path="/area-clienti/contabilita" element={<DocumentsPage />} />
              <Route path="/area-clienti/rma" element={<RMAPage />} />
              <Route path="/area-clienti/rma/da-documento" element={<RMADocumentSelectPage />} />
              <Route path="/area-clienti/rma/da-documento/:tipork/:anno/:serie/:numdoc" element={<RMADocumentItemsPage />} />
              <Route path="/area-clienti/rma/cerca" element={<RMASearchPage />} />
              <Route path="/area-clienti/rma/preparazione" element={<RMACartPage />} />
              <Route path="/area-clienti/rma/:rmaId" element={<RMADetailPage />} />
              <Route path="/area-clienti/rma/:rmaId/ordine" element={<RMAOrderPage />} />
              <Route path="/area-clienti/avvisi" element={<NotificationsPage />} />
              <Route path="/area-clienti/operatori" element={<OperatoriPage />} />
              <Route path="/area-clienti/preferenze" element={<PreferenzePage />} />
              <Route path="/area-clienti/in-attesa" element={<WaitingListPage />} />
            </Route>
            {/* Checkout fuori dal layout (richiede full-width).
                `cliente_subordinato` è ammesso: escluderlo dalla rotta
                rendeva il checkout irraggiungibile anche per l'operatore
                «Crea ordine», con un redirect muto e nessuna spiegazione.
                Chi non può inviare (`vedi-sito`, `crea-carrello`) entra e
                trova il motivo scritto — vedi `canPlaceOrder` in
                CheckoutPage. */}
            <Route path="/area-clienti/checkout" element={<ProtectedRoute roles={['cliente','agente','admin','cliente_subordinato']}><CheckoutPage /></ProtectedRoute>} />

            {/* Pagamento con carta. Fuori da /area-clienti perché sono le
                rotte su cui rientra il browser dal dominio Nexi: restano
                protette, ma il loro percorso è quello configurato sul
                terminale (NEXI_RETURN_BASE_URL) e non va cambiato senza
                aggiornare anche quello. */}
            <Route path="/pagamento/redirect" element={<ProtectedRoute roles={['cliente','agente','admin','cliente_subordinato']}><PaymentRedirectPage /></ProtectedRoute>} />
            <Route path="/pagamento/esito" element={<ProtectedRoute roles={['cliente','agente','admin','cliente_subordinato']}><PaymentResultPage /></ProtectedRoute>} />
            <Route path="/pagamento/annullato" element={<ProtectedRoute roles={['cliente','agente','admin','cliente_subordinato']}><PaymentCancelledPage /></ProtectedRoute>} />

            {/* Admin / Backoffice — richiede ruolo admin */}
            <Route path="/admin/notifiche" element={<ProtectedRoute roles={['admin', 'admin_operatore']}><AdminLayout><AdminNotificationsPage /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/homepage-prodotti" element={<ProtectedRoute roles={['admin', 'admin_operatore']} permission="contenuti"><AdminLayout><AdminHomepageProductsPage /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin" element={<ProtectedRoute roles={['admin', 'admin_operatore']}><AdminHome /></ProtectedRoute>} />
            <Route path="/admin/banner" element={<ProtectedRoute roles={['admin', 'admin_operatore']} permission="contenuti"><AdminLayout><AdminBannerPage /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/prodotti" element={<ProtectedRoute roles={['admin', 'admin_operatore']} permission="catalogo"><AdminLayout><AdminProductsPage /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/prodotti/:sku" element={<ProtectedRoute roles={['admin', 'admin_operatore']} permission="catalogo"><AdminLayout><AdminProductDetailPage /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/carriere" element={<ProtectedRoute roles={['admin', 'admin_operatore']} permission="carriere"><AdminLayout><AdminCareersPage /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/carriere/:posId" element={<ProtectedRoute roles={['admin', 'admin_operatore']} permission="carriere"><AdminLayout><AdminCareerDetailPage /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/alert-stock" element={<ProtectedRoute roles={['admin', 'admin_operatore']} permission="catalogo"><AdminLayout><AdminAlertStockPage /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/difformita" element={<ProtectedRoute roles={['admin', 'admin_operatore']} permission="segnalazioni"><AdminLayout><AdminDifformityPage /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/difformita/:reportId" element={<ProtectedRoute roles={['admin', 'admin_operatore']} permission="segnalazioni"><AdminLayout><AdminDifformityDetailPage /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/rma" element={<ProtectedRoute roles={['admin', 'admin_operatore']} permission="resi_ordini"><AdminLayout><AdminRMAPage /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/rma/:numero" element={<ProtectedRoute roles={['admin', 'admin_operatore']} permission="resi_ordini"><AdminLayout><AdminRMADetailPage /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/ordini" element={<ProtectedRoute roles={['admin', 'admin_operatore']} permission="resi_ordini"><AdminLayout><AdminOrdersPage /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/carrelli" element={<ProtectedRoute roles={['admin', 'admin_operatore']} permission="resi_ordini"><AdminLayout><AdminCarrelliPage /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/ordini/piattaforma/:idOrdine" element={<ProtectedRoute roles={['admin', 'admin_operatore']} permission="resi_ordini"><AdminLayout><AdminOrderDetailPage /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/ordini/erp/:tipork/:anno/:serie/:numdoc" element={<ProtectedRoute roles={['admin', 'admin_operatore']} permission="resi_ordini"><AdminLayout><AdminErpOrderDetailPage /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/clienti" element={<ProtectedRoute roles={['admin', 'admin_operatore']} permission="clienti"><AdminLayout><AdminClientsPage /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/clienti/:anConto" element={<ProtectedRoute roles={['admin', 'admin_operatore']} permission="clienti"><AdminLayout><AdminClientDetailPage /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/clienti/:anConto/accessi" element={<ProtectedRoute roles={['admin', 'admin_operatore']} permission="clienti"><AdminLayout><AdminClientAccessHistoryPage /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/registrazioni" element={<ProtectedRoute roles={['admin', 'admin_operatore']} permission="clienti"><AdminLayout><AdminRegistrationsPage /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/registrazioni/:userId" element={<ProtectedRoute roles={['admin', 'admin_operatore']} permission="clienti"><AdminLayout><AdminRegistrationDetailPage /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/condizioni-commerciali" element={<ProtectedRoute roles={['admin', 'admin_operatore']} permission="clienti"><AdminLayout><AdminPricingOverviewPage /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/impersona" element={<ProtectedRoute roles={['admin', 'admin_operatore']} permission="clienti"><AdminLayout><AdminImpersonatePage /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/abbinatore-v2/*" element={<ProtectedRoute roles={['admin', 'admin_operatore']} permission="abbinatore"><AbbinatoreV2Routes /></ProtectedRoute>} />
            <Route path="/admin/team" element={<ProtectedRoute roles={['admin']}><AdminLayout><AdminTeamPage /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/sync-erp" element={<ProtectedRoute roles={['admin', 'admin_operatore']} permission="sistema"><AdminLayout><AdminAggiornamentiPage /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/configurazione" element={<ProtectedRoute roles={['admin', 'admin_operatore']} permission="sistema"><AdminLayout><AdminConfigPage /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/email" element={<ProtectedRoute roles={['admin', 'admin_operatore']} permission="sistema"><AdminLayout><AdminEmailSettingsPage /></AdminLayout></ProtectedRoute>} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </main>
        <ConditionalFooter />
        <CartDrawer />
        <RMACartFloating />
        <CompareBar />
        <CompareDrawer />
      </div>
      </CompareProvider>
      </RecentlyViewedProvider>
      </MachinesProvider>
      </FavoritesProvider>
      </RMACartProvider>
      </CartProvider>
      </AdminNotificationsProvider>
      </NotificationsProvider>
      </AdminProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
